// Global
import React from 'react';
import { RichTextField, LinkField, Text } from '@sitecore-jss/sitecore-jss-nextjs';

// Lib
import { Profiles } from 'lib/templates/Feature.BOKF.model';

// Local
import RichTextA11yWrapper from 'helpers/RichTextA11yWrapper/RichTextA11yWrapper';
import Button from 'helpers/Button/Button';
import { useDictionary } from 'lib/hooks/use-dictionary';
import LinkWrapper from 'helpers/LinkWrapper/LinkWrapper';
import ImageWrapper from 'helpers/ImageWrapper/ImageWrapper';
import useExperienceEditor from 'lib/use-experience-editor';

export type BankerProfileProps = Profiles.DataTypes.BankerProfile;
export type TableType = { [key: string]: string } | object;

const BankerProfile = (props: BankerProfileProps): JSX.Element => {
  const dict = useDictionary();

  const bankerProfileBlend =
    dict?.['Feature.Profiles.BankerProfileBlend'] ||
    'Our online mortgage lending platform is hosted by Blend Labs, Inc.';

  const bankerProfileConnect = dict?.['Feature.Profiles.BankerProfileConnect'] || 'Connect';

  const bankerProfileGetStarted =
    dict?.['Feature.Profiles.BankerProfileGetStarted'] || 'Apply now or sign in';

  const bankerProfileGetStartedSection =
    dict?.['Feature.Profiles.BankerProfileGetStartedSection'] ||
    "After applying, we'll contact you to discuss loan details and answer any questions. You can connect with your mortgage banker using the contact information above for even more personalized service and advice.";

  const bankerProfileGetStartedTitle =
    dict?.['Feature.Profiles.BankerProfileGetStartedTitle'] || 'Are you ready to get started?';

  const bankerProfileNMLS = dict?.['Feature.Profiles.BankerProfileNMLS'] || 'NMLS #';

  const {
    Image,
    FirstName,
    LastName,
    JobTitle,
    Logo,
    Phone,
    Mobile,
    Fax,
    Email,
    Address1,
    Address2,
    City,
    State,
    Zip,
    Link,
    ApplicationLink,
    ProfileBio,
    NMLS,
  } = props?.fields || {};

  const phone: LinkField = {
    value: {
      href: `tel:+1${Phone?.value.replace(/-/g, '').trim()}`,
      text: Phone?.value,
    },
  };

  const mobile: LinkField = {
    value: {
      href: `tel:+1${Mobile?.value.replace(/-/g, '').trim()}`,
      text: Mobile?.value,
    },
  };

  const fax: LinkField = {
    value: {
      href: `tel:+1${Fax?.value.replace(/-/g, '').trim()}`,
      text: Fax?.value,
    },
  };

  const email: LinkField = {
    value: {
      href: `mailto:${Email?.value}`,
      text: Email?.value,
    },
  };

  const isEE = useExperienceEditor();

  return (
    <section className="component container ">
      <div className="flex flex-col lg:flex-row p-4 md:p-6 lg:p-8 gap-y-4 md:gap-y-8 gap-x-6 bg-light-background-neutral-variant border border-light-stroke-primary rounded">
        {/* left side longform */}
        <div className="flex flex-col xs:flex-row lg:flex-col gap-x-6 gap-y-8 lg:gap-y-6">
          <ImageWrapper
            field={Image}
            height="258"
            width="208"
            className="w-full xs:w-1/3 lg:w-full object-cover"
          />
          <div className="flex flex-col w-full xs:w-2/3 lg:w-full gap-2 justify-center lg:justify-normal">
            <h4 className="flex flex-row text-light-fill-tertiary-brand">
              <RichTextA11yWrapper field={FirstName} /> &nbsp;
              <RichTextA11yWrapper field={LastName} />
            </h4>
            <div className="p2-medium">
              <RichTextA11yWrapper field={JobTitle?.fields.Value as RichTextField} />
            </div>
            {(NMLS?.value !== '' || isEE) && (
              <div className="flex flex-row p3 text-light-fill-primary-variant">
                <RichTextA11yWrapper field={{ value: bankerProfileNMLS }} />
                <RichTextA11yWrapper field={NMLS} />
              </div>
            )}

            <ImageWrapper field={Logo} width="208" height="54" className="mt-4" />
          </div>
        </div>
        {/* main content */}
        <div className="flex flex-col w-full gap-y-4">
          <div className="p1-medium">
            <RichTextA11yWrapper field={{ value: bankerProfileConnect }} />
          </div>
          <div className="flex flex-col sm:flex-row gap-y-3 gap-x-6 w-full">
            {/* contact info */}
            <div className="flex flex-col gap-y-3 flex-1">
              {(Phone?.value !== '' || isEE) && (
                <div className="flex flex-row text-light-fill-tertiary-brand gap-x-2 p2-medium">
                  T
                  {isEE ? (
                    <Text field={Phone} />
                  ) : (
                    <LinkWrapper
                      className="p2 text-light-fill-secondary lg:text-light-fill-primary"
                      field={phone}
                    />
                  )}
                </div>
              )}

              {(Mobile?.value !== '' || isEE) && (
                <div className="flex flex-row text-light-fill-tertiary-brand gap-x-2 p2-medium">
                  M
                  {isEE ? (
                    <Text field={Mobile} />
                  ) : (
                    <LinkWrapper
                      className="p2 text-light-fill-secondary lg:text-light-fill-primary"
                      field={mobile}
                    />
                  )}
                </div>
              )}
              {(Fax?.value !== '' || isEE) && (
                <div className="flex flex-row text-light-fill-tertiary-brand gap-x-2 p2-medium">
                  F{isEE ? <Text field={Fax} /> : <LinkWrapper className="p2" field={fax} />}
                </div>
              )}

              {(Email?.value !== '' || isEE) && (
                <>
                  {isEE ? (
                    <Text field={Email} />
                  ) : (
                    <LinkWrapper className="p2-medium link" field={email} />
                  )}
                </>
              )}
            </div>
            {/* address, linkedin, CTA */}
            <div className="flex flex-col flex-1 gap-y-4">
              <div>
                {Address1 && <RichTextA11yWrapper className="p2" tag="div" field={Address1} />}
                {Address2 && <RichTextA11yWrapper className="p2" tag="div" field={Address2} />}

                <div className="flex">
                  <RichTextA11yWrapper className="p2" tag="div" field={City} />
                  , &nbsp;
                  <RichTextA11yWrapper
                    className="p2"
                    tag="div"
                    field={State?.fields.StateName as RichTextField}
                  />
                  &nbsp;
                  <RichTextA11yWrapper className="p2" tag="div" field={Zip} />
                </div>
              </div>
              <LinkWrapper className="p2-medium link" field={Link || {}} />
              <Button
                type="default"
                size="full"
                tag="a"
                href={ApplicationLink?.value.href}
                label={bankerProfileGetStarted}
                fieldClass={ApplicationLink?.value?.class || ''}
              />
              <RichTextA11yWrapper tag="div" field={{ value: bankerProfileBlend }} className="c1" />
            </div>
          </div>
          <hr className="solid divide-light-stroke-primary"></hr>
          <RichTextA11yWrapper
            className="p2 rtaw text-light-fill-primary-variant"
            field={ProfileBio}
          />
          <RichTextA11yWrapper tag="h3" field={{ value: bankerProfileGetStartedTitle }} />
          <RichTextA11yWrapper
            field={{ value: bankerProfileGetStartedSection }}
            className="p2 text-light-fill-primary-variant"
          />
          <div className="">
            <Button
              type="auxiliary"
              tag="a"
              href={ApplicationLink?.value.href}
              label={bankerProfileGetStarted}
              fieldClass={ApplicationLink?.value?.class || ''}
            />
          </div>
          <RichTextA11yWrapper tag="div" field={{ value: bankerProfileBlend }} className="c1" />
        </div>
      </div>
    </section>
  );
};

export default BankerProfile;
